import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/auth'
import { authApi } from '@/api'

export interface LogoutOptions {
  redirect?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<User | null>(null)
  const token = ref<string>('')
  const permissions = ref<string[]>([])

  // 计算属性
  const isAuthenticated = computed(() => !!token.value)
  const userType = computed(() => user.value?.userType || 0)
  const isAdmin = computed(() => userType.value === 1)
  const isMerchant = computed(() => userType.value === 2)

  const resolvePermissions = (type: number) => {
    const platformPermissions = [
      'platform:view',
      'platform:merchant:view',
      'platform:module:view',
      'platform:user:view',
    ]

    const merchantAdminPermissions = [
      'merchant:view',
      'merchant:config:view',
      'merchant:member:view',
      'merchant:points:view',
      'merchant:level:view',
      'merchant:shop:view',
      'merchant:employee:view',
      'merchant:mall:view',
      'merchant:sms:view',
      'merchant:child:view',
      'merchant:subject-doc:view',
      'merchant:service:view',
      'merchant:outstanding:view',
      'merchant:news:view',
      'merchant:recommended:view',
    ]

    const storeAdminPermissions = [
      'merchant:view',
      'merchant:config:view',
      'merchant:member:view',
      'merchant:points:view',
      'merchant:level:view',
      'merchant:employee:view',
      'merchant:mall:view',
      'merchant:child:view',
      'merchant:service:view',
      'merchant:outstanding:view',
      'merchant:news:view',
      'merchant:recommended:view',
    ]

    const storeStaffPermissions = [
      'merchant:view',
      'merchant:member:view',
      'merchant:points:view',
      'merchant:mall:view',
      'merchant:child:view',
      'merchant:service:view',
      'merchant:outstanding:view',
      'merchant:news:view',
      'merchant:recommended:view',
    ]

    if (type === 1) return platformPermissions
    if (type === 2) return merchantAdminPermissions
    if (type === 3) return storeAdminPermissions
    if (type === 4) return storeStaffPermissions
    return []
  }

  // 方法
  const login = async (loginData: { mobile: string; password: string }) => {
    try {
      const response = await authApi.login(loginData)
      
      if (response.success) {
        const { user: userData, accessToken } = response.data
        
        const userPermissions = resolvePermissions(userData.userType)
        
        // 设置认证信息
        setAuth(userData, accessToken, userPermissions)
        
        return { success: true }
      } else {
        return { success: false, message: response.message || '登录失败' }
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      return { success: false, message: error.message || '登录失败' }
    }
  }

  const redirectToLogin = () => {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  }

  const logout = async (options: LogoutOptions = {}) => {
    const shouldRedirect = options.redirect !== false
    try {
      // 调用退出登录API
      if (token.value) {
        await authApi.logout()
      }
    } catch (error) {
      console.error('Logout API failed:', error)
    } finally {
      // 清理本地状态
      user.value = null
      token.value = ''
      permissions.value = []
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')

      if (shouldRedirect) {
        redirectToLogin()
      }
    }
  }

  const setAuth = (userData: User, authToken: string, userPermissions: string[]) => {
    user.value = userData
    token.value = authToken
    permissions.value = userPermissions
    
    // 持久化存储
    localStorage.setItem('auth_token', authToken)
    localStorage.setItem('auth_user', JSON.stringify(userData))
  }

  const hasPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(permission => 
      permissions.value.includes(permission)
    )
  }

  const initAuth = async () => {
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('auth_user')
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser)
        token.value = savedToken
        user.value = userData
        
        const defaultPermissions = resolvePermissions(userData.userType)
        permissions.value = defaultPermissions
        
        // 验证token是否仍然有效，获取最新用户信息
        try {
          const profileResponse = await authApi.getProfile()
          if (profileResponse.success) {
            user.value = profileResponse.data
            permissions.value = resolvePermissions(profileResponse.data.userType)
          }
        } catch (error) {
          console.error('Failed to refresh profile:', error)
          // Token可能已过期，清理认证状态
          logout({ redirect: false })
        }
      } catch (error) {
        console.error('Failed to restore auth:', error)
        logout({ redirect: false })
      }
    }
  }

  return {
    // 状态
    user,
    token,
    permissions,
    // 计算属性
    isAuthenticated,
    userType,
    isAdmin,
    isMerchant,
    // 方法
    login,
    logout,
    setAuth,
    hasPermissions,
    initAuth,
  }
})
